import { create } from 'zustand';
import { apiClient } from '@/lib/api/api-client';
import { TOKEN_KEY } from '@/lib/utils/constants';
import { slugDeLaClave } from '@/lib/utils/portal-storage';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { MISIONES, type IdDeMision } from './misiones';

/**
 * Mini Prixia (Ronda X): el avance de las misiones vive en el SERVIDOR, por empresa
 * (`GET`/`PUT /api/demo/misiones`): sigue en otra computadora y el equipo de Prixia lo
 * ve. Acá queda lo que trajo el servidor más lo que pasó en esta pestaña (unión: nunca
 * resta). Sin persistencia en el navegador. Va por empresa como la marca demo (I6).
 */
export interface ProgresoDeMisiones {
  empezadas: string[];
  hechas: string[];
}

export const SIN_PROGRESO: ProgresoDeMisiones = { empezadas: [], hechas: [] };

/** El único conteo: lo que muestra la tarjeta y lo que anima el logo salen de acá. */
export const misionesHechas = (p: ProgresoDeMisiones) =>
  MISIONES.filter((m) => p.hechas.includes(m.id)).length;

/** La misión que se acaba de cumplir en esta pestaña, para animarla una sola vez. */
export interface RecienCumplida {
  empresa: string;
  /** Cuántas misiones hay hechas contándola. */
  enTotal: number;
}

const RUTA = '/api/demo/misiones';

export const misionesDemoApi = {
  leer: () => apiClient.get<ProgresoDeMisiones>(RUTA),
  marcar: (mision: IdDeMision, evento: 'empezada' | 'hecha') =>
    apiClient.put<ProgresoDeMisiones>(`${RUTA}/${mision}`, { evento }),
};

/** La empresa de esta pestaña: la misma que usa el token. */
export function empresaDeLaSesion(): string {
  return slugDeLaClave(TOKEN_KEY) ?? '';
}

/** Soporte («Entrar como») mira, no recorre: no avanza ni informa misiones de la persona. */
const enSoporte = () => useAuthStore.getState().isImpersonating;

const unir = (a: string[], b: string[]) => [...a, ...b.filter((x) => !a.includes(x))];
const sumar = (lista: string[], id: string) => unir(lista, [id]);

interface ProgresoState {
  porEmpresa: Record<string, ProgresoDeMisiones>;
  /** Las empresas cuyo avance ya contestó el servidor en esta pestaña. */
  conocidas: string[];
  /**
   * La escribe sólo `hacer`, y sólo si el avance es conocido: sin eso el total sería
   * inventado. `cargar` no la toca: por eso recargar no anima, ni otra pestaña tampoco.
   */
  recienCumplida: RecienCumplida | null;
  olvidarRecienCumplida: () => void;
  /**
   * Trae el avance del servidor y lo une con lo de la pestaña. Devuelve `true` si el
   * servidor contestó (sin respuesta no se sabe si hay misiones empezadas).
   */
  cargar: () => Promise<boolean>;
  /** Se abrió su recorrido: se ve al instante y se informa al servidor. */
  empezar: (mision: IdDeMision) => void;
  /**
   * Quedó hecha. `informar`: sólo las de pantalla (el servidor anota solo las demás y
   * rechaza que el cliente las dé por hechas). Devuelve `true` si recién ahora quedó hecha.
   */
  hacer: (mision: IdDeMision, informar: boolean) => boolean;
}

export const useProgresoDeMisiones = create<ProgresoState>()((set, get) => {
  const de = (empresa: string) => get().porEmpresa[empresa] ?? SIN_PROGRESO;
  const poner = (empresa: string, p: ProgresoDeMisiones) =>
    set((s) => ({ porEmpresa: { ...s.porEmpresa, [empresa]: p } }));
  const informar = (mision: IdDeMision, evento: 'empezada' | 'hecha') =>
    void misionesDemoApi.marcar(mision, evento).catch(() => {
      // Si no llegó, la pantalla sigue igual; la próxima carga trae lo que el servidor tenga.
    });

  return {
    porEmpresa: {},
    conocidas: [],
    recienCumplida: null,
    olvidarRecienCumplida: () => set({ recienCumplida: null }),
    cargar: async () => {
      const empresa = empresaDeLaSesion();
      try {
        const servidor = await misionesDemoApi.leer();
        const p = de(empresa);
        poner(empresa, {
          empezadas: unir(p.empezadas, servidor?.empezadas ?? []),
          hechas: unir(p.hechas, servidor?.hechas ?? []),
        });
        set((s) => ({ conocidas: sumar(s.conocidas, empresa) }));
        return true;
      } catch {
        // Sin avance del servidor la tarjeta arranca con lo de la pestaña.
        return false;
      }
    },
    empezar: (mision) => {
      if (enSoporte()) return;
      const empresa = empresaDeLaSesion();
      const p = de(empresa);
      poner(empresa, { ...p, empezadas: sumar(p.empezadas, mision) });
      informar(mision, 'empezada');
    },
    hacer: (mision, avisarAlServidor) => {
      if (enSoporte()) return false;
      const empresa = empresaDeLaSesion();
      const p = de(empresa);
      if (p.hechas.includes(mision)) return false;
      const nuevo = { empezadas: sumar(p.empezadas, mision), hechas: [...p.hechas, mision] };
      poner(empresa, nuevo);
      if (get().conocidas.includes(empresa))
        set({ recienCumplida: { empresa, enTotal: misionesHechas(nuevo) } });
      if (avisarAlServidor) informar(mision, 'hecha');
      return true;
    },
  };
});

/** El total de la misión recién cumplida en la empresa de esta pestaña, o `null`. */
export function useRecienCumplida(): number | null {
  return useProgresoDeMisiones((s) =>
    s.recienCumplida?.empresa === empresaDeLaSesion() ? s.recienCumplida.enTotal : null
  );
}

/** El avance de la empresa de esta pestaña. */
export function useProgresoDeLaEmpresa(): ProgresoDeMisiones {
  return useProgresoDeMisiones((s) => s.porEmpresa[empresaDeLaSesion()] ?? SIN_PROGRESO);
}
